"use client";

import { HTMLAttributes, useEffect, useMemo } from "react";
import { SizesUI } from "./SizesUI";
import { LegacySizesUI } from "./Legacy/LegacySizesUI";
import { getLegacySizeProps } from "./Legacy/getLegacySizeProps";
import { useAvailableSizes } from "./utils";
import { hasSizes } from "@/utils/product/hasSizes";
import { FitToolAndSizeChart } from "@/components/FitToolAndSizeChart/FitToolAndSizeChart";
import { ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED } from "@/constants/constants";
import { getBasket } from "@/utils/analytics/getBasket";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { Variant } from "@/types/types";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { StandardProps } from "@/components/componentMap.ts";

export const Sizes = (props: StandardProps & { sizes: string }) => {
  const { product, configProps, flags } = props;

  const { variant } = useVariant(product);

  const sizesList = useAvailableSizes(
    product,
    { sortSizes: configProps?.sortSizes === true },
    { showPromoOnIndicators: !!flags.showPromoOnIndicators.value },
  );

  useEffect(() => {
    const sizeAddedToBasket = () => {
      const { product: basketProducts } = getBasket();
      const sizesForProduct = basketProducts.filter(
        (item) => item.productId === product.aliases.pimProductId,
      );

      const colourProducts = product.variants.filter(
        (v: Variant) =>
          v?.differentiators?.colour === variant.differentiators?.colour &&
          sizesForProduct.some((item) => item.sku === v.aliases.skuId),
      );

      if (colourProducts.length > 2) {
        dispatchMonetateQEvent("MultipleSizesOfSameColourA2B");
      } else if (colourProducts.length > 1) {
        dispatchMonetateQEvent("2SizesOfSameColourA2B");
      }
    };
    window.addEventListener(
      ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
      sizeAddedToBasket,
    );
    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        sizeAddedToBasket,
      );
    };
  }, []);

  const FitToolAndSizeChartComponent = useMemo(() => {
    return function FitToolAndSizeChartComponent(
      htmlProps: HTMLAttributes<HTMLDivElement>,
    ) {
      return (
        <div {...htmlProps}>
          <FitToolAndSizeChart {...props} />
        </div>
      );
    };
  }, [props]);

  if (!hasSizes(product)) {
    return null;
  }

  const sharedProps = {
    isGroupingAllowed: configProps?.allowGrouping === true,
    showMore:
      typeof configProps?.showMore === "number"
        ? configProps.showMore
        : undefined,
    replaceHistoryEntryOnVariantChange:
      flags.replaceHistoryEntryOnVariantChange.value === true,
    FitToolAndSizeChartComponent,
    withFitToolAndSizeChart: configProps?.withFitToolAndSizeChart === true,
  };

  if (flags.useNewSizeSelector.value === true) {
    return <SizesUI sizes={sizesList} {...sharedProps} />;
  }

  return (
    <LegacySizesUI
      {...sharedProps}
      {...getLegacySizeProps({
        sizes: sizesList,
        product,
        configProps,
        flags,
      })}
    />
  );
};
